import React, { Fragment, useState, useRef, useEffect } from "react";

import {
  IconButton,
  TextField,
  Box,
  Paper,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { Message, Close, Send } from "@mui/icons-material";
import configServ from "../../services/config";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import LoaderNew from "../Loader/LoaderNew";

export default function ChatWindow() {
  const navigate = useNavigate(); // Initialize useNavigate
  const { company_id } = useSelector((state) => state.GlobalVariables);
  const [customers, setCustomers] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { sender: "bot", text: "Hello! How can I assist you?" },
  ]);
  const [input, setInput] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [chatType, setChatType] = useState("New Order");
  // const [productData, setProductData] = useState([]);
  const [loader, setLoader] = useState(false);

  const handleSendMessage = async () => {
    if (input.trim() === "") return;

    // Check if selectedCustomer is null or undefined
    if (!selectedCustomer && chatType !== "general") {
      alert("Please select a customer before sending a message.");
      return;
    }

    const newMessages = [...messages, { sender: "user", text: input }];
    setMessages(newMessages);
    setInput("");

    const data = {
      input: input,
      selectedCustomer: selectedCustomer,
      customerId: selectedCustomer?.id,
      company_id: company_id,
    };

    try {
      setLoader(true); // ✅ Loader ON before API call
      const result =
        chatType === "general"
          ? await configServ.generalChat(data)
          : await configServ.chat(data);

      if (result.status === "success") {
        const productData = result.data;
        setTimeout(() => {
          const botReply = getBotReply(input);
          setMessages([
            ...newMessages,
            { sender: "bot", text: result.message },
          ]);

          // Redirect only if a customer is selected
          if (selectedCustomer) {
            setTimeout(() => {
              if (chatType === "New Order") {
                navigate(`/sales-order`, {
                  state: { selectedCustomer, productData },
                });
              } else if (chatType === "New Invoice") {
                navigate(`/invoice`, { state: { selectedCustomer } });
              }
              setSelectedCustomer(null);
              setIsOpen(false);
            }, 1000); // Delay for bot reply before redirect
          }
        }, 1000);
      }
    } catch (e) {
      console.log("Error in chat API:", e);
    } finally {
      setLoader(false); // ✅ Loader OFF after API response/error
    }
  };

  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [messages]);

  const getBotReply = (message) => {
    const responses = {
      hello: "Hello! How are you?",
      hi: "Hi there! Need any help?",
      bye: "Goodbye! Have a great day!",
      thanks: "You're welcome! 😊",
    };
    return responses[message.toLowerCase()] || "I'm just a demo bot! 😊";
  };

  const fetchCustomerList = async () => {
    try {
      const result = await configServ.get_customer({ company_id: company_id });
      setCustomers(result);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    fetchCustomerList();
  }, [isOpen]);

  // Highlight function to mark matching text
  const highlightMatch = (text, query) => {
    const safeText = typeof text === "string" ? text : "";
    const safeQuery = typeof query === "string" ? query : "";

    const words = safeQuery.toLowerCase().split(" ").filter(Boolean);
    if (!words.length) return safeText;

    const regex = new RegExp(`(${words.join("|")})`, "gi");

    return safeText.split(regex).map((part, index) =>
      words.includes(part.toLowerCase()) ? (
        <mark key={index} style={{ backgroundColor: "#ffe58f" }}>
          {part}
        </mark>
      ) : (
        <React.Fragment key={index}>{part}</React.Fragment>
      )
    );
  };

  return (
    <Box position="fixed" bottom={20} right={20} zIndex={1000}>
      {isOpen ? (
        <Paper
          elevation={6}
          sx={{
            width: { xs: "90vw", sm: 400 },
            height: { xs: "70vh", sm: 400 },
            p: 2,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "white",
            zIndex: 1000,
          }}
        >
          {/* Chat Header with Dropdowns */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom={1}
            pb={1}
          >
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Chat Type</InputLabel>
              <Select
                value={chatType}
                onChange={(e) => setChatType(e.target.value)}
              >
                <MenuItem value="New Invoice">New Invoice</MenuItem>
                <MenuItem value="New Order">New Order</MenuItem>
                <MenuItem value="general">General</MenuItem>
              </Select>
            </FormControl>
            <IconButton onClick={() => setIsOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          {/* Customer Selection with Autocomplete */}
          {chatType !== "general" && (
            <Autocomplete
              options={customers}
              getOptionLabel={(option) => option.fname || ""}
              value={selectedCustomer}
              onChange={(event, newValue) => setSelectedCustomer(newValue)}
              filterOptions={(options, { inputValue }) => {
                const words = inputValue
                  .toLowerCase()
                  .split(" ")
                  .filter(Boolean);
                return options.filter((option) => {
                  const name = option.fname?.toLowerCase() || "";
                  return words.every((word) => name.includes(word));
                });
              }}
              renderOption={(props, option, { inputValue }) => (
                <li {...props}>
                  <Box
                    sx={{
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                      maxWidth: "100%",
                    }}
                  >
                    <Typography variant="body2">
                      {highlightMatch(option.fname, inputValue)}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Customer"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ my: 1 }}
                />
              )}
            />
          )}

          {/* Chat Messages with Loader */}
          <Box
            flex={1}
            p={1}
            sx={{
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {loader && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <LoaderNew size={30} />
              </Box>
            )}
            {!loader &&
              messages.map((msg, index) => (
                <Typography
                  key={index}
                  align={msg.sender === "user" ? "right" : "left"}
                  sx={{
                    backgroundColor:
                      msg.sender === "user" ? "primary.light" : "grey.200",
                    color: "black",
                    borderRadius: 2,
                    px: 1,
                    py: 0.5,
                    display: "inline-block",
                    maxWidth: "75%",
                    alignSelf:
                      msg.sender === "user" ? "flex-end" : "flex-start",
                  }}
                >
                  {msg.text}
                </Typography>
              ))}
            <div ref={messagesEndRef} />
          </Box>

          {/* Message Input Box */}
          <Box display="flex" borderTop={1} pt={1}>
            <TextField
              fullWidth
              placeholder="Type a message..."
              variant="outlined"
              size="small"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={loader}
            >
              <Send />
            </IconButton>
          </Box>
        </Paper>
      ) : (
        // Floating Chat Button (Hidden when chat is open)
        <IconButton
          onClick={() => setIsOpen(true)}
          color="primary"
          sx={{
            position: "fixed",
            bottom: { xs: 10, sm: 20 },
            right: { xs: 10, sm: 20 },
            backgroundColor: "primary.main",
            color: "white",
            boxShadow: 3,
            "&:hover": {
              backgroundColor: "white",
              color: "primary.main",
            },
          }}
        >
          <Message />
        </IconButton>
      )}
    </Box>
  );
}
